import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, themes } from '../contexts/ThemeContext';
import { languageOptions } from '../i18n/translations';
import { Settings, Sun, Moon, Monitor, Globe, ChevronDown, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export default function SettingsPanel({ isOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme, isDark } = useTheme();
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  const currentLanguage = languageOptions.find(l => l.code === i18n.language) || languageOptions[0];
  const isRtl = currentLanguage?.rtl;

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    const selectedLang = languageOptions.find(l => l.code === langCode);
    // Update document direction for RTL languages
    document.documentElement.dir = selectedLang?.rtl ? 'rtl' : 'ltr';
    setLanguageDropdownOpen(false);
  };

  const themeOptions = [
    { value: 'dark', label: t('settings.dark_mode'), icon: Moon },
    { value: 'light', label: t('settings.light_mode'), icon: Sun },
    { value: 'system', label: t('settings.system'), icon: Monitor }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <Card className={`w-full max-w-md ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Settings className="w-5 h-5 text-blue-500" />
            </div>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('settings.title')}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className={isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-6">
          {/* Theme Section */}
          <div>
            <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              {t('settings.appearance')}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = theme === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setTheme(option.value)}
                    className={`p-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-blue-500/20 border-blue-500 text-blue-500'
                        : isDark
                        ? 'bg-zinc-800/50 border-zinc-700 text-slate-400 hover:border-zinc-600'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Language Section */}
          <div>
            <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              {t('settings.language')}
            </h3>
            <div className="relative">
              <button
                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Globe className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                  <span className={`text-lg ${isRtl ? 'mr-2' : ''}`}>{currentLanguage.flag}</span>
                  <div className="text-left">
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {currentLanguage.nativeName}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {currentLanguage.name}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''} ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
              </button>

              {/* Language Dropdown */}
              {languageDropdownOpen && (
                <div className={`absolute top-full left-0 right-0 mt-2 rounded-lg border shadow-lg overflow-hidden z-10 max-h-64 overflow-y-auto ${
                  isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'
                }`}>
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full flex items-center justify-between p-3 transition-colors ${
                        i18n.language === lang.code
                          ? isDark ? 'bg-blue-500/20' : 'bg-blue-50'
                          : isDark ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{lang.flag}</span>
                        <div className="text-left">
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {lang.nativeName}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            {lang.name}
                          </p>
                        </div>
                      </div>
                      {i18n.language === lang.code && (
                        <Check className="w-5 h-5 text-blue-500" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
          <Button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {t('close')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
